import { useState, useCallback } from 'react';
import { Quote, WalkInAlbumQuote, WalkInPPQuote } from '../types';
import { STORAGE_KEYS, loadFromStorage, saveToStorage, generateQuoteNumber, normalizeGeneralPlannerQuote } from '../utils/helpers';

export function useQuotes() {
  const [quotes, setQuotes] = useState<Quote[]>(() =>
    loadFromStorage<Quote[]>(STORAGE_KEYS.QUOTES, [])
  );
  const [walkInAlbumQuotes, setWalkInAlbumQuotes] = useState<WalkInAlbumQuote[]>(() =>
    loadFromStorage<WalkInAlbumQuote[]>(STORAGE_KEYS.WALK_IN_ALBUM, [])
  );
  const [walkInPPQuotes, setWalkInPPQuotes] = useState<WalkInPPQuote[]>(() =>
    loadFromStorage<WalkInPPQuote[]>(STORAGE_KEYS.WALK_IN_PP, [])
  );
  const [generalPlannerQuotes, setGeneralPlannerQuotes] = useState<any[]>(() =>
    loadFromStorage<any[]>(STORAGE_KEYS.GENERAL_PLANNER, [])
  );

  const saveQuote = useCallback((quote: Quote) => {
    setQuotes(prev => {
      const existing = prev.findIndex(q => q.id === quote.id);
      const updated = existing >= 0
        ? prev.map(q => q.id === quote.id ? { ...quote, updatedAt: new Date().toISOString() } : q)
        : [{ ...quote, quoteNumber: quote.quoteNumber || generateQuoteNumber(), createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }, ...prev];
      saveToStorage(STORAGE_KEYS.QUOTES, updated);
      return updated;
    });
  }, []);

  const deleteQuote = useCallback((id: string) => {
    setQuotes(prev => {
      const updated = prev.filter(q => q.id !== id);
      saveToStorage(STORAGE_KEYS.QUOTES, updated);
      return updated;
    });
  }, []);

  // Creates the invoice and marks it sent to the customer. This does NOT
  // imply payment — advance may arrive immediately or days later. Status
  // only moves to 'sent' here (unless already further along); 'confirmed'
  // is reserved for when the advance is actually confirmed received via
  // markAdvanceReceived below.
  const convertQuoteToInvoice = useCallback((id: string, invoiceNumber: string) => {
    setQuotes(prev => {
      const updated = prev.map(q => q.id === id
        ? {
            ...q,
            documentType: 'invoice' as const,
            invoiceNumber,
            status: q.status === 'draft' ? 'sent' : q.status,
            updatedAt: new Date().toISOString(),
          }
        : q);
      saveToStorage(STORAGE_KEYS.QUOTES, updated);
      return updated;
    });
  }, []);

  // Separate, explicit confirmation that the advance has actually landed.
  // This — not invoice creation — is what should gate booking/lifecycle
  // creation (Customer Specific Document, Stage 1, etc.).
  const markAdvanceReceived = useCallback((id: string) => {
    setQuotes(prev => {
      const updated = prev.map(q => q.id === id
        ? {
            ...q,
            advanceReceived: true,
            advanceReceivedAt: new Date().toISOString(),
            status: 'confirmed',
            updatedAt: new Date().toISOString(),
          } as any
        : q);
      saveToStorage(STORAGE_KEYS.QUOTES, updated);
      return updated;
    });
  }, []);

  const updateQuoteStatus = useCallback((id: string, status: string) => {
    setQuotes(prev => {
      const updated = prev.map(q => q.id === id ? { ...q, status, updatedAt: new Date().toISOString() } : q);
      saveToStorage(STORAGE_KEYS.QUOTES, updated);
      return updated;
    });
  }, []);

  const saveWalkInAlbumQuote = useCallback((quote: WalkInAlbumQuote) => {
    setWalkInAlbumQuotes(prev => {
      const existing = prev.findIndex(q => q.id === quote.id);
      const updated = existing >= 0
        ? prev.map(q => q.id === quote.id ? quote : q)
        : [{ ...quote, quoteNumber: generateQuoteNumber(), createdAt: new Date().toISOString() }, ...prev];
      saveToStorage(STORAGE_KEYS.WALK_IN_ALBUM, updated);
      return updated;
    });
  }, []);

  const saveWalkInPPQuote = useCallback((quote: WalkInPPQuote) => {
    setWalkInPPQuotes(prev => {
      const existing = prev.findIndex(q => q.id === quote.id);
      const updated = existing >= 0
        ? prev.map(q => q.id === quote.id ? quote : q)
        : [{ ...quote, quoteNumber: generateQuoteNumber(), createdAt: new Date().toISOString() }, ...prev];
      saveToStorage(STORAGE_KEYS.WALK_IN_PP, updated);
      return updated;
    });
  }, []);

  const saveGeneralPlannerQuote = useCallback((quote: any) => {
    setGeneralPlannerQuotes(prev => {
      const existing = prev.findIndex(q => q.id === quote.id);
      const updated = existing >= 0
        ? prev.map(q => q.id === quote.id ? { ...quote, updatedAt: new Date().toISOString() } : q)
        : [{ ...quote, createdAt: quote.createdAt || new Date().toISOString() }, ...prev];
      saveToStorage(STORAGE_KEYS.GENERAL_PLANNER, updated);
      return updated;
    });
  }, []);

  const deleteGeneralPlannerQuote = useCallback((id: string) => {
    setGeneralPlannerQuotes(prev => {
      const updated = prev.filter(q => q.id !== id);
      saveToStorage(STORAGE_KEYS.GENERAL_PLANNER, updated);
      return updated;
    });
  }, []);

  const updateGeneralPlannerStatus = useCallback((id: string, status: string) => {
    setGeneralPlannerQuotes(prev => {
      const updated = prev.map(q => q.id === id ? { ...q, status, updatedAt: new Date().toISOString() } : q);
      saveToStorage(STORAGE_KEYS.GENERAL_PLANNER, updated);
      return updated;
    });
  }, []);

  // Combined view for Dashboard / Quote History / Booking Calendar — General
  // Planner quotes are wrapped into the standard Quote shape so they show up
  // alongside catalog quotes, sorted newest first.
  const allQuotesForDisplay: Quote[] = [...quotes, ...generalPlannerQuotes.map(normalizeGeneralPlannerQuote)]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Route deletion to the correct underlying store based on id prefix.
  const deleteAnyQuote = useCallback((id: string) => {
    if (id.startsWith('gp_')) deleteGeneralPlannerQuote(id);
    else deleteQuote(id);
  }, [deleteGeneralPlannerQuote, deleteQuote]);

  // Same routing for status changes, used by the quick-look preview.
  const updateAnyQuoteStatus = useCallback((id: string, status: string) => {
    if (id.startsWith('gp_')) updateGeneralPlannerStatus(id, status);
    else updateQuoteStatus(id, status);
  }, [updateGeneralPlannerStatus, updateQuoteStatus]);

  return {
    quotes, saveQuote, deleteQuote, updateQuoteStatus,
    walkInAlbumQuotes, saveWalkInAlbumQuote,
    walkInPPQuotes, saveWalkInPPQuote,
    generalPlannerQuotes, saveGeneralPlannerQuote, deleteGeneralPlannerQuote, updateGeneralPlannerStatus,
    allQuotesForDisplay, deleteAnyQuote, updateAnyQuoteStatus, convertQuoteToInvoice, markAdvanceReceived,
  };
}
